import {
  App,
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { ExportButton } from '@/shared/components/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  financeApi,
  type ReconciliationFilter,
  type ReconciliationRow,
  type ReconciliationView,
} from '@/api/finance';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const PKR = (n: number | string | null | undefined) =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function FinanceReconciliationPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasAuthority('FINANCE:EDIT'));

  const [bankId, setBankId] = useState<number | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [filter, setFilter] = useState<ReconciliationFilter>('UNRECONCILED');
  const [statementBalance, setStatementBalance] = useState<number | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<number[]>([]);

  const banksQuery = useQuery({
    queryKey: ['finance', 'bank-accounts'],
    queryFn: () => financeApi.listBankAccounts(),
  });

  const params = bankId
    ? {
        bankAccountId: bankId,
        from: range[0].format('YYYY-MM-DD'),
        to: range[1].format('YYYY-MM-DD'),
        filter,
      }
    : null;

  const view = useQuery<ReconciliationView>({
    queryKey: ['finance', 'reconciliation', params],
    queryFn: () => financeApi.reconciliation(params!),
    enabled: !!params,
  });

  const mark = useMutation({
    mutationFn: ({ ids, reconciled }: { ids: number[]; reconciled: boolean }) =>
      financeApi.markReconciled(ids, reconciled),
    onSuccess: (count, vars) => {
      message.success(`${count} entr${count === 1 ? 'y' : 'ies'} ${vars.reconciled ? 'reconciled' : 'unmarked'}`);
      setSelectedKeys([]);
      qc.invalidateQueries({ queryKey: ['finance', 'reconciliation'] });
    },
    onError: (e) => message.error(extractError(e, 'Mark failed')),
  });

  const data = view.data;

  // Bank options exclude cash accounts (cash isn't reconcilable).
  const bankOptions =
    banksQuery.data
      ?.filter((b) => !b.cash && b.active)
      .map((b) => ({ value: b.id, label: b.accountName })) ?? [];

  const variance = useMemo(() => {
    if (!data || statementBalance === null) return null;
    return statementBalance - Number(data.reconciledBalance);
  }, [data, statementBalance]);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="Finance"
        title="Bank reconciliation"
        description="Tick off ledger entries that match the bank statement. Reconciled balance + variance update live so you can match against the statement total at the end of the period."
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/reconciliation.xlsx',
              params: params ?? {},
              fileName: params ? `reconciliation-${params.from}-to-${params.to}.xlsx` : 'reconciliation.xlsx',
              disabled: !data || !params,
            }]}
          />
        }
      />

      <Card>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={8}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Bank account
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Choose bank"
              options={bankOptions}
              value={bankId}
              onChange={(v) => {
                setBankId(v);
                setSelectedKeys([]);
              }}
              loading={banksQuery.isLoading}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} md={8}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Statement period
            </Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              value={range}
              onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])}
              allowClear={false}
            />
          </Col>
          <Col xs={24} md={8}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Statement closing balance (optional)
            </Typography.Text>
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter to compute live variance"
              value={statementBalance}
              onChange={(v) => setStatementBalance(typeof v === 'number' ? v : null)}
              precision={2}
              addonBefore="₨"
            />
          </Col>
        </Row>
      </Card>

      {!data && (
        <Card>
          <Empty description="Choose a bank account to load the reconciliation view." />
        </Card>
      )}

      {data && (
        <>
          <Card>
            <Row gutter={[16, 12]}>
              <Col xs={12} md={5}>
                <Statistic title="Opening" value={PKR(data.openingBalance)} prefix="₨" />
              </Col>
              <Col xs={12} md={5}>
                <Statistic title="Book balance" value={PKR(data.bookBalance)} prefix="₨" />
              </Col>
              <Col xs={12} md={5}>
                <Statistic title="Reconciled balance" value={PKR(data.reconciledBalance)} prefix="₨" />
              </Col>
              <Col xs={12} md={5}>
                <Statistic
                  title="Unreconciled"
                  value={PKR(data.unreconciledTotal)}
                  prefix="₨"
                  styles={{ content: {
                    color: Math.abs(Number(data.unreconciledTotal)) > 0.01 ? '#cf1322' : undefined,
                  } }}
                  suffix={
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                      ({data.unreconciledCount})
                    </Typography.Text>
                  }
                />
              </Col>
              <Col xs={12} md={4}>
                <Statistic
                  title="Variance vs statement"
                  value={variance === null ? '—' : PKR(variance)}
                  prefix={variance === null ? undefined : '₨'}
                  styles={{ content: {
                    color:
                      variance === null
                        ? undefined
                        : Math.abs(variance) < 0.01
                        ? '#389e0d'
                        : '#cf1322',
                  } }}
                />
              </Col>
            </Row>
          </Card>

          <Card
            title={
              <Segmented<ReconciliationFilter>
                value={filter}
                onChange={(v) => {
                  setFilter(v);
                  setSelectedKeys([]);
                }}
                options={[
                  { value: 'UNRECONCILED', label: 'Unreconciled' },
                  { value: 'RECONCILED', label: 'Reconciled' },
                  { value: 'ALL', label: 'All' },
                ]}
              />
            }
            extra={
              <Space>
                <Typography.Text type="secondary">{selectedKeys.length} selected</Typography.Text>
                <Button
                  icon={<CheckOutlined />}
                  type="primary"
                  disabled={!canEdit || selectedKeys.length === 0}
                  loading={mark.isPending}
                  onClick={() => mark.mutate({ ids: selectedKeys, reconciled: true })}
                >
                  Mark reconciled
                </Button>
                <Popconfirm
                  title="Unmark these entries?"
                  okText="Unmark"
                  okButtonProps={{ danger: true }}
                  disabled={!canEdit || selectedKeys.length === 0}
                  onConfirm={() => mark.mutate({ ids: selectedKeys, reconciled: false })}
                >
                  <Button
                    icon={<CloseOutlined />}
                    danger
                    disabled={!canEdit || selectedKeys.length === 0}
                  >
                    Unmark
                  </Button>
                </Popconfirm>
              </Space>
            }
          >
            {!canEdit && (
              <Alert
                type="info"
                showIcon
                message="View-only — marking entries requires FINANCE:EDIT."
                style={{ marginBottom: 12 }}
              />
            )}
            <Table<ReconciliationRow>
              rowKey="entryId"
              dataSource={data.rows}
              loading={view.isFetching}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true }}
              rowSelection={{
                selectedRowKeys: selectedKeys,
                onChange: (keys) => setSelectedKeys(keys.map(Number)),
              }}
              columns={[
                { title: 'Date', dataIndex: 'entryDate', width: 110, fixed: 'left' as const },
                { title: 'Description', dataIndex: 'description', ellipsis: true },
                { title: 'Account', dataIndex: 'accountName', width: 180 },
                {
                  title: 'Source',
                  dataIndex: 'sourceType',
                  width: 130,
                  render: (v: string) => <Tag>{v}</Tag>,
                },
                {
                  title: 'Debit',
                  dataIndex: 'amount',
                  align: 'right' as const,
                  width: 110,
                  render: (v: string, row) => (row.entryType === 'DEBIT' ? PKR(v) : ''),
                },
                {
                  title: 'Credit',
                  dataIndex: 'amount',
                  align: 'right' as const,
                  width: 110,
                  render: (v: string, row) => (row.entryType === 'CREDIT' ? PKR(v) : ''),
                },
                {
                  title: 'Status',
                  dataIndex: 'reconciled',
                  width: 130,
                  render: (v: boolean, row) =>
                    row.reversal ? (
                      <Tag color="purple">Reversal</Tag>
                    ) : v ? (
                      <Tag color="green">Reconciled</Tag>
                    ) : (
                      <Tag>Pending</Tag>
                    ),
                },
              ]}
              scroll={{ x: 900 }}
            />
          </Card>
        </>
      )}
    </div>
  );
}
